import { EyeOffIcon, LinkIcon, TrashIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import type { ComponentType, SVGProps } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import Halftone from '@/components/agents/Halftone';
import {
  ArrowGlyph,
  delay,
  SectionHeading,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { COMPLIANCE, ShieldGlyph, useLandingT } from './kit';

const TRUST_CENTER_URL = 'https://easypeasyai.trust.site/';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** What happens to a recording, for people transcribing private conversations. */
export default function Privacy() {
  const { t } = useLandingT();
  const reveal = useReveal<HTMLDivElement>(0.2);

  const promises: { icon: Icon; title: string; detail: string }[] = [
    {
      icon: EyeOffIcon,
      title: t('Not used to train our models'),
      detail: t(
        'We don’t use your recordings or transcripts to train our own models.',
      ),
    },
    {
      icon: LinkIcon,
      title: t('Private until you share'),
      detail: t(
        'Only you can open a transcript, unless you turn on its share link or share it with your team.',
      ),
    },
    {
      icon: TrashIcon,
      title: t('Your files, your call'),
      detail: t(
        'Delete a transcription and its recording is removed from our storage.',
      ),
    },
  ];

  return (
    <section className='relative isolate overflow-hidden bg-[#0c0b1c] py-20 sm:py-28'>
      <Halftone
        at='100% 0%'
        className='opacity-30 [--ht:rgb(var(--tw-color-primary-800))]'
        reach={70}
      />
      <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
          <div className='lg:col-span-5'>
            <SectionHeading
              invert
              size='md'
              title={t('Private conversations stay private.')}
              lead={t(
                'Interviews, patient sessions and board meetings are transcribed under the same security standards our business customers ask for.',
              )}
            />
            <ul className='mt-8 flex flex-wrap gap-2' role='list'>
              {COMPLIANCE.map((name) => (
                <li
                  key={name}
                  className='inline-flex items-center gap-2 rounded-full bg-white/[0.06] px-3.5 py-1.5 text-sm font-semibold text-white ring-1 ring-inset ring-white/15'
                >
                  <ShieldGlyph className='h-4 w-4 text-emerald-300' />
                  {name}
                </li>
              ))}
            </ul>
            <p className='mt-6 max-w-md text-[15px] leading-7 text-[#b7b7d2]'>
              {t(
                'Healthcare teams can sign our Business Associate Agreement (BAA) on any paid plan.',
              )}
            </p>
            <a
              href={TRUST_CENTER_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='mt-8 inline-flex items-center gap-2 rounded-full px-5 py-3 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/25 transition duration-200 hover:bg-white/10 hover:ring-white/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
            >
              {t('Visit the Trust Center')}
              <ArrowGlyph className='-rotate-45 rtl:rotate-45' />
            </a>
          </div>

          <div
            ref={reveal.ref}
            data-reveal={reveal.state}
            className={clsxm(
              styles.reveal,
              'divide-y divide-white/10 border-y border-white/10 lg:col-span-7',
            )}
          >
            {promises.map(({ icon: PromiseIcon, title, detail }, index) => (
              <div
                key={title}
                className={clsxm(styles.rv, 'flex gap-5 py-7')}
                style={delay(index * 100)}
              >
                <span className='flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-gradient-to-b from-white/15 to-white/[0.04] ring-1 ring-inset ring-white/15'>
                  <PromiseIcon className='h-5 w-5 text-white' aria-hidden />
                </span>
                <div>
                  <p className='text-lg font-semibold tracking-tight text-white'>
                    {title}
                  </p>
                  <p className='mt-1.5 text-[15px] leading-7 text-[#b7b7d2]'>
                    {detail}
                  </p>
                </div>
              </div>
            ))}
            <div className='py-6'>
              <Link
                href='/sub-processors'
                className='group inline-flex items-center gap-1.5 text-sm font-semibold text-[#c9c9e4] hover:text-white'
              >
                {t('See every provider that processes your data')}
                <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
